<template>
  <div>
    <EnvironmentUnavailable v-if="envUnavailable" class="mb-6" />

    <!-- Bandeau secret révélé une seule fois -->
    <div
      v-if="revealedSecret"
      class="bg-emerald-50 border border-emerald-300 rounded-xl p-5 mb-6"
    >
      <div class="flex items-start gap-3">
        <Icon icon="solar:key-bold" class="w-6 h-6 text-emerald-700 flex-shrink-0 mt-0.5" />
        <div class="flex-1 min-w-0">
          <h3 class="font-semibold text-emerald-900 mb-1">Votre nouvelle clé API</h3>
          <p class="text-sm text-emerald-800 mb-3">
            Copiez-la maintenant : pour des raisons de sécurité, elle ne sera plus jamais affichée.
          </p>
          <div class="flex items-center gap-2">
            <code class="flex-1 bg-white border border-emerald-200 rounded-lg px-3 py-2 text-sm font-mono break-all">
              {{ revealedSecret }}
            </code>
            <button
              @click="copy(revealedSecret)"
              class="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 flex-shrink-0"
            >
              {{ copied ? 'Copié ✓' : 'Copier' }}
            </button>
          </div>
        </div>
        <button @click="revealedSecret = ''" class="text-emerald-700 hover:text-emerald-900 flex-shrink-0">
          <Icon icon="solar:close-circle-bold" class="w-5 h-5" />
        </button>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex items-center justify-between mb-6">
      <p class="text-sm text-gray-600">
        Les clés de cet écran sont des clés
        <strong>{{ modeStore.isTest ? 'de test (yk_test_…)' : 'de production (yk_live_…)' }}</strong>,
        utilisables uniquement contre
        <code class="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{{ baseUrl }}</code>.
      </p>
      <button
        @click="showCreateModal = true"
        :disabled="authStore.isSuspended"
        class="px-4 py-2 bg-[#8C0004] text-white rounded-lg text-sm font-medium hover:bg-red-900 disabled:opacity-50 transition-colors flex items-center gap-2"
      >
        <Icon icon="solar:add-circle-bold" class="w-4 h-4" />
        Nouvelle clé
      </button>
    </div>

    <!-- Tableau -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-50 border-b border-gray-200">
            <tr>
              <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Nom</th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Clé</th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Dernière utilisation</th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Créée le</th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-if="loading" v-for="i in 3" :key="`s-${i}`" class="animate-pulse">
              <td colspan="5" class="px-6 py-4"><div class="h-4 bg-gray-100 rounded"></div></td>
            </tr>

            <tr v-else-if="keys.length === 0">
              <td colspan="5" class="px-6 py-16 text-center">
                <Icon icon="solar:key-bold" class="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p class="text-gray-500">Aucune clé API dans cet environnement.</p>
              </td>
            </tr>

            <tr v-else v-for="k in keys" :key="k.id" :class="['hover:bg-gray-50 transition-colors', k.revokedAt ? 'opacity-50' : '']">
              <td class="px-6 py-4 text-sm font-medium text-gray-900">{{ k.name }}</td>
              <td class="px-6 py-4">
                <code class="text-sm font-mono text-gray-600">{{ k.prefix }}…</code>
              </td>
              <td class="px-6 py-4 text-sm text-gray-600">
                {{ k.lastUsedAt ? formatDate(k.lastUsedAt) : 'Jamais utilisée' }}
              </td>
              <td class="px-6 py-4 text-sm text-gray-600">{{ formatDate(k.createdAt) }}</td>
              <td class="px-6 py-4">
                <span v-if="k.revokedAt" class="px-3 py-1 rounded-full text-xs font-semibold bg-gray-200 text-gray-600">
                  Révoquée
                </span>
                <button
                  v-else-if="!authStore.isSuspended"
                  @click="revoke(k)"
                  class="px-3 py-1 border border-[#8C0004] text-[#8C0004] rounded text-xs font-medium hover:bg-red-50"
                >
                  Révoquer
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal création -->
    <div
      v-if="showCreateModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="showCreateModal = false"
    >
      <div class="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        <div class="p-6 border-b border-gray-200 flex items-center justify-between">
          <h3 class="text-lg font-bold text-gray-900">Nouvelle clé API</h3>
          <button @click="showCreateModal = false" class="text-gray-400 hover:text-gray-600">
            <Icon icon="solar:close-circle-bold" class="w-6 h-6" />
          </button>
        </div>
        <form @submit.prevent="create" class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Nom de la clé
            </label>
            <input
              v-model="newKeyName"
              type="text"
              maxlength="100"
              required
              placeholder="Ex: Serveur de production"
              class="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8C0004]"
            />
            <p class="text-xs text-gray-500 mt-2">
              Ce libellé vous aide à identifier la clé : donnez-lui le nom du service qui l'utilisera.
            </p>
          </div>

          <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-3">
            <p class="text-sm text-red-600">{{ error }}</p>
          </div>

          <div class="flex gap-3 pt-2">
            <button
              type="button"
              @click="showCreateModal = false"
              class="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              :disabled="!newKeyName || creating"
              class="flex-1 px-4 py-2.5 bg-[#8C0004] text-white rounded-lg text-sm font-medium hover:bg-red-900 disabled:opacity-50"
            >
              {{ creating ? 'Création...' : 'Créer la clé' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { Icon } from '@iconify/vue'
import { apiFor, apiBaseUrlFor, isEnvironmentUnauthorized } from '../services/api'
import EnvironmentUnavailable from '../components/EnvironmentUnavailable.vue'
import { useModeStore } from '../store/mode'
import { useAuthStore } from '../store/auth'
import { useNotification } from '../composables/useNotification'

const modeStore = useModeStore()
const authStore = useAuthStore()
const notify = useNotification()

const keys = ref<any[]>([])
const loading = ref(true)
const envUnavailable = ref(false)
const showCreateModal = ref(false)
const newKeyName = ref('')
const creating = ref(false)
const error = ref('')
const revealedSecret = ref('')
const copied = ref(false)

const baseUrl = computed(() => apiBaseUrlFor(modeStore.mode))

onMounted(load)

async function load() {
  loading.value = true
  try {
    const { data } = await apiFor(modeStore.mode).get('/partner-portal/api-keys')
    keys.value = data.keys || []
  } catch (err) {
    envUnavailable.value = isEnvironmentUnauthorized(err)
    console.error('Erreur chargement clés:', err)
    keys.value = []
  } finally {
    loading.value = false
  }
}

async function create() {
  creating.value = true
  error.value = ''
  try {
    const { data } = await apiFor(modeStore.mode).post('/partner-portal/api-keys', {
      name: newKeyName.value,
    })
    revealedSecret.value = data.secret
    showCreateModal.value = false
    newKeyName.value = ''
    await load()
  } catch (err: any) {
    error.value = err.response?.data?.message || 'Erreur lors de la création'
  } finally {
    creating.value = false
  }
}

async function revoke(key: any) {
  if (
    !window.confirm(
      `Révoquer la clé « ${key.name} » ? Toute requête l'utilisant sera immédiatement refusée.`
    )
  ) {
    return
  }
  try {
    await apiFor(modeStore.mode).delete(`/partner-portal/api-keys/${key.id}`)
    notify.success('Clé révoquée')
    await load()
  } catch (err: any) {
    notify.error(err.response?.data?.message || 'Erreur lors de la révocation')
  }
}

async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    copied.value = true
    setTimeout(() => (copied.value = false), 2000)
  } catch {
    notify.error('Copie impossible — sélectionnez le texte manuellement')
  }
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}
</script>
